import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Check, Edit2, Minus, Pencil, Plus, Tag, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";
import { EditItemVariationsModal } from "./EditItemVariationsModal";

export function OrderItemsTable({
  order,
  canEditItems,
  editingItems,
  localItems,
  setLocalItems,
  setEditingItems,
  startEditItems,
  saveItemChanges,
  openAddProduct,
  changeQty,
  removeItem,
  subtotal,
  shipping,
  formatPrice,
  showDiscountForm,
  setShowDiscountForm,
  discountInput,
  setDiscountInput,
  discountType,
  setDiscountType,
  discountReason,
  setDiscountReason,
  handleApplyDiscount,
  applyingDiscount,
  setApplyingDiscount,
  authAxios,
  onUpdate,
}) {
  const [editingVariationItem, setEditingVariationItem] = useState(null);
  const [editingVariationItemIdx, setEditingVariationItemIdx] = useState(null);
  const [isSavingVariations, setIsSavingVariations] = useState(false);

  const handleEditVariations = (item, idx) => {
    setEditingVariationItem(item);
    setEditingVariationItemIdx(idx);
  };

  const handleSaveVariations = (updatedItem) => {
    setIsSavingVariations(true);
    try {
      // Use real index to handle duplicate products correctly
      const itemIdx = editingVariationItemIdx ?? localItems.findIndex(i => i.product_id === updatedItem.product_id);
      if (itemIdx >= 0) {
        // Recalculate unit_price based on selected_variations using Number() for price safety
        const newUnitPrice = updatedItem.selected_variations.reduce((sum, v) => sum + (Number(v.price) || 0), 0);
        const updated = { ...updatedItem, unit_price: newUnitPrice };
        setLocalItems(prev => prev.map((it, i) => i === itemIdx ? updated : it));
        toast.success("Componentes atualizados");
      }
    } catch (e) {
      console.error("Error saving variations:", e);
      toast.error("Erro ao atualizar");
    } finally {
      setIsSavingVariations(false);
      setEditingVariationItem(null);
      setEditingVariationItemIdx(null);
    }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-xs text-gray-500 uppercase tracking-wider">ITENS DO PEDIDO</h3>
        {canEditItems && !editingItems && (
          <Button variant="ghost" size="sm" className="text-xs text-emerald-600 gap-1 h-7" onClick={startEditItems} data-testid="edit-items-btn">
            <Pencil className="w-3 h-3" />Editar
          </Button>
        )}
        {editingItems && (
          <div className="flex gap-1">
            <Button size="sm" className="text-xs h-7 bg-emerald-600 hover:bg-emerald-700 gap-1" onClick={saveItemChanges} data-testid="save-items-btn">
              <Check className="w-3 h-3" />Guardar
            </Button>
            <Button variant="ghost" size="sm" className="text-xs h-7 text-gray-500" onClick={() => setEditingItems(false)}>Cancelar</Button>
          </div>
        )}
      </div>
      <div className="space-y-2">
        {(editingItems ? localItems : order.items).map((item, idx) => (
          <div key={item.product_id ? `${item.product_id}-${item.notes || ""}-${idx}` : idx} className="flex items-center justify-between py-1 group">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {editingItems && (
                <div className="flex items-center gap-1 shrink-0">
                  <button className="w-6 h-6 rounded bg-gray-100 hover:bg-gray-200 flex items-center justify-center" onClick={() => changeQty(idx, -1)} data-testid={`qty-minus-${idx}`}>
                    <Minus className="w-3 h-3 text-gray-600" />
                  </button>
                  <span className="text-sm font-bold w-6 text-center">{item.quantity}</span>
                  <button className="w-6 h-6 rounded bg-gray-100 hover:bg-gray-200 flex items-center justify-center" onClick={() => changeQty(idx, 1)} data-testid={`qty-plus-${idx}`}>
                    <Plus className="w-3 h-3 text-gray-600" />
                  </button>
                </div>
              )}
              <div className="min-w-0">
                {(() => {
                  const rawName = item.product_name || "";
                  const vars = item.selected_variations?.length > 0
                    ? item.selected_variations
                    : (() => { const m = rawName.match(/\(([^)]+)\)$/); return m ? m[1].split(", ").map(v => ({ name: v.trim(), quantity: 1 })) : []; })();
                  const baseName = vars.length > 0 ? rawName.replace(/\s*\([^)]+\)$/, "") : rawName;
                  return (
                    <>
                      <p className="text-sm font-bold text-gray-900 truncate">{!editingItems && `${item.quantity} `}{baseName}</p>
                      {vars.length > 0 && (
                        <div className="mt-0.5 pl-1.5 border-l-2 border-emerald-200">
                          {vars.map((v, vi) => (
                            <span key={vi} className="text-xs text-gray-500 block">{(v.quantity || 1) > 1 ? `${v.quantity}× ` : ""}{v.name}</span>
                          ))}
                          {editingItems && vars.length > 0 && (
                            <button
                              onClick={() => handleEditVariations(localItems[idx], idx)}
                              className="text-xs text-emerald-600 hover:text-emerald-700 font-medium mt-1 flex items-center gap-1"
                              data-testid={`edit-variations-btn-${idx}`}
                            >
                              <Edit2 className="w-3 h-3" />
                              Editar
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}
                {item.notes && <p className="text-xs text-red-600 font-medium mt-0.5">* {item.notes}</p>}
                {item.category && <span className="inline-block text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 mt-0.5">{item.category}</span>}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-sm font-medium text-gray-700">{formatPrice(item.quantity * item.unit_price)}</span>
              {editingItems && (
                <button className="w-6 h-6 rounded hover:bg-red-50 flex items-center justify-center" onClick={() => removeItem(idx)} data-testid={`remove-item-${idx}`}>
                  <Trash2 className="w-3.5 h-3.5 text-red-400 hover:text-red-600" />
                </button>
              )}
            </div>
          </div>
        ))}
        {editingItems && (
          <Button variant="outline" className="w-full text-xs gap-1 border-dashed border-emerald-300 text-emerald-600 hover:bg-emerald-50 mt-2" onClick={openAddProduct} data-testid="add-item-btn">
            <Plus className="w-3.5 h-3.5" />ADICIONAR ITEM
          </Button>
        )}
        <Separator className="my-2" />
        <div className="space-y-1 text-sm">
          <div className="flex justify-between text-gray-500"><span className="min-w-0 truncate mr-2">Subtotal</span><span className="shrink-0">{formatPrice(editingItems ? localItems.reduce((s, i) => s + i.quantity * i.unit_price, 0) : subtotal)}</span></div>
          {shipping > 0 && <div className="flex justify-between text-gray-500"><span className="min-w-0 truncate mr-2">Frete</span><span className="shrink-0">{formatPrice(shipping)}</span></div>}
          {(order.discount_amount > 0) && (
            <div className="flex justify-between text-emerald-600">
              <span className="flex items-center gap-1 min-w-0 truncate mr-2"><Tag className="w-3 h-3 shrink-0" />Desconto{order.discount_reason ? ` (${order.discount_reason})` : ""}</span>
              <span className="shrink-0">-{formatPrice(order.discount_amount)}</span>
            </div>
          )}
          <div className="flex justify-between font-bold text-base text-gray-900 pt-1"><span className="min-w-0 truncate mr-2">Total</span><span className="shrink-0" data-testid="order-total">{formatPrice(editingItems ? Math.max(0, localItems.reduce((s, i) => s + i.quantity * i.unit_price, 0) + shipping - (order.discount_amount || 0)) : order.total)}</span></div>
        </div>

        {!showDiscountForm ? (
          <button
            onClick={() => {
              setDiscountInput(order.discount_amount > 0 ? String(order.discount_amount) : "");
              setDiscountType(order.discount_type || "fixed");
              setDiscountReason(order.discount_reason || "");
              setShowDiscountForm(true);
            }}
            className="mt-2 flex items-center gap-1.5 text-xs text-gray-500 hover:text-emerald-600 font-medium transition"
            data-testid="open-discount-form"
          >
            <Tag className="w-3.5 h-3.5" />
            {order.discount_amount > 0 ? "Editar desconto" : "Aplicar desconto"}
          </button>
        ) : (
          <div className="mt-2 border border-emerald-200 rounded-xl p-3 bg-emerald-50 space-y-2">
            <p className="text-xs font-semibold text-emerald-700 flex items-center gap-1"><Tag className="w-3 h-3" /> Desconto</p>
            <div className="flex gap-1.5">
              <button onClick={() => setDiscountType("fixed")} className={`text-xs px-2.5 py-1.5 rounded border font-semibold transition ${discountType === "fixed" ? "bg-emerald-500 border-emerald-500 text-white" : "border-gray-300 text-gray-500 bg-white"}`}>€</button>
              <button onClick={() => setDiscountType("percent")} className={`text-xs px-2.5 py-1.5 rounded border font-semibold transition ${discountType === "percent" ? "bg-emerald-500 border-emerald-500 text-white" : "border-gray-300 text-gray-500 bg-white"}`}>%</button>
              <Input type="number" min="0" step="0.01" value={discountInput} onChange={e => setDiscountInput(e.target.value)} placeholder={discountType === "percent" ? "0 %" : "0.00 €"} className="flex-1 h-8 text-sm bg-white" data-testid="order-discount-input" />
            </div>
            <Input type="text" value={discountReason} onChange={e => setDiscountReason(e.target.value)} placeholder="Motivo (opcional)" className="h-8 text-xs bg-white" data-testid="order-discount-reason" />
            {(() => {
              const num = parseFloat(discountInput) || 0;
              const computed = discountType === "percent" ? Math.min(subtotal, subtotal * num / 100) : Math.min(subtotal, num);
              const newTotal = Math.max(0, subtotal + shipping - computed);
              return (
                <div className="text-xs rounded-lg bg-white border border-emerald-200 px-2.5 py-2 space-y-0.5">
                  <div className="flex justify-between text-gray-500"><span>Subtotal</span><span>{formatPrice(subtotal)}</span></div>
                  {shipping > 0 && <div className="flex justify-between text-gray-400"><span>Frete</span><span>{formatPrice(shipping)}</span></div>}
                  {num > 0 && <div className="flex justify-between text-emerald-600 font-medium"><span>Desconto</span><span>-{formatPrice(Math.round(computed * 100) / 100)}</span></div>}
                  <div className="flex justify-between font-bold text-gray-900 border-t pt-1 mt-1"><span>{num > 0 ? "Novo Total" : "Total atual"}</span><span>{formatPrice(newTotal)}</span></div>
                </div>
              );
            })()}
            <div className="flex gap-2">
              <Button size="sm" className="flex-1 h-8 bg-emerald-500 hover:bg-emerald-600 text-white text-xs" onClick={handleApplyDiscount} disabled={applyingDiscount} data-testid="apply-discount-btn">{applyingDiscount ? "..." : "Aplicar"}</Button>
              {order.discount_amount > 0 && (
                <Button size="sm" variant="outline" className="h-8 text-xs text-red-500 border-red-200 hover:bg-red-50" onClick={async () => {
                  setApplyingDiscount(true);
                  try {
                    await authAxios.patch(`/orders/${order.id}/discount`, { discount_amount: 0, discount_type: "fixed", discount_value: 0, discount_reason: "" });
                    toast.success("Desconto removido");
                    setShowDiscountForm(false);
                    onUpdate();
                  } catch {
                    toast.error("Erro ao remover desconto");
                  }
                  setApplyingDiscount(false);
                }}>Remover</Button>
              )}
              <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setShowDiscountForm(false)}>Cancelar</Button>
            </div>
          </div>
        )}
      </div>

      {/* Modal para editar variações de menu composto */}
      <EditItemVariationsModal
        isOpen={!!editingVariationItem}
        onClose={() => {
          setEditingVariationItem(null);
          setEditingVariationItemIdx(null);
        }}
        item={editingVariationItem}
        onSave={handleSaveVariations}
        isSaving={isSavingVariations}
      />
    </div>
  );
}
